import { memo, useMemo, useId, useRef } from 'react';

type BreathingPhase = 'inhale' | 'hold1' | 'hold2' | 'exhale';

type LinePhaseSegment = {
  key: BreathingPhase;
  durationMs: number;
};

type PhaseInterval = LinePhaseSegment & { start: number; end: number };

const VIEWBOX_WIDTH = 300;
const VIEWBOX_HEIGHT = 100;
const CENTER_X = VIEWBOX_WIDTH / 2;
const BASE_Y = 85;
const PEAK_Y = 25;
const PIXELS_PER_MS = 0.025; // Speed of scroll

const FALLBACK_PHASES: LinePhaseSegment[] = [
  { key: 'inhale', durationMs: 4000 },
  { key: 'exhale', durationMs: 4000 },
];

const gradientColorMap: Record<string, { from: string; to: string }> = {
  'from-teal-400 to-cyan-500': { from: '#2dd4bf', to: '#06b6d4' },
  'from-blue-400 to-teal-400': { from: '#60a5fa', to: '#2dd4bf' },
  'from-cyan-500 to-blue-500': { from: '#06b6d4', to: '#3b82f6' },
  'from-blue-500 to-indigo-500': { from: '#3b82f6', to: '#6366f1' },
};

const fallbackGradient = { from: '#0ea5e9', to: '#6366f1' };

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const lerp = (start: number, end: number, t: number) =>
  start + (end - start) * clamp01(t);

interface LineBreathingProps {
  isActive: boolean;
  currentPhase: BreathingPhase;
  phaseIndex: number;
  totalPhases: number;
  phaseLabel: string;
  timeRemaining: number;
  introTimeRemaining: number;
  introVoiceOn: boolean;
  phaseProgressRatio: number;
  gradientClass: string;
  linePhases: LinePhaseSegment[];
}

const getY = (
  t: number,
  cycleData: { phases: PhaseInterval[]; totalDuration: number }
) => {
  const { phases, totalDuration } = cycleData;
  if (totalDuration === 0) return BASE_Y;

  // Handle negative time by wrapping correctly
  const timeInCycle = ((t % totalDuration) + totalDuration) % totalDuration;

  const phase =
    phases.find((p) => timeInCycle >= p.start && timeInCycle < p.end) ||
    phases[phases.length - 1];

  if (!phase) return BASE_Y;

  const progress = (timeInCycle - phase.start) / phase.durationMs;

  switch (phase.key) {
    case 'inhale':
      return lerp(BASE_Y, PEAK_Y, progress);
    case 'hold1':
      return PEAK_Y;
    case 'exhale':
      return lerp(PEAK_Y, BASE_Y, progress);
    case 'hold2':
      return BASE_Y;
    default:
      return BASE_Y;
  }
};

const LineBreathing = memo(function LineBreathing({
  isActive,
  currentPhase,
  phaseIndex,
  totalPhases,
  phaseLabel,
  timeRemaining,
  introTimeRemaining,
  phaseProgressRatio,
  introVoiceOn,
  gradientClass,
  linePhases,
}: LineBreathingProps) {
  const gradientStops = gradientColorMap[gradientClass] ?? fallbackGradient;
  const gradientId = useId();

  const prevPhaseIndexRef = useRef(phaseIndex);
  const totalCyclesRef = useRef(0);

  // Reset logic
  if (!isActive && phaseIndex === 0 && prevPhaseIndexRef.current !== 0) {
    totalCyclesRef.current = 0;
  }

  // Increment logic
  if (
    isActive &&
    phaseIndex === 0 &&
    prevPhaseIndexRef.current === totalPhases - 1
  ) {
    totalCyclesRef.current += 1;
  }

  prevPhaseIndexRef.current = phaseIndex;

  const cycleData = useMemo(() => {
    const phases = linePhases?.length ? linePhases : FALLBACK_PHASES;
    const totalDuration = phases.reduce((sum, p) => sum + p.durationMs, 0);
    let accumulated = 0;
    const phaseIntervals = phases.map((p) => {
      const start = accumulated;
      accumulated += p.durationMs;
      return { ...p, start, end: accumulated };
    });
    return { phases: phaseIntervals, totalDuration };
  }, [linePhases]);

  // Calculate total elapsed time
  const currentCycleTime = useMemo(() => {
    if (!isActive) return 0;
    const phases = cycleData.phases;
    const currentPhaseData = phases[phaseIndex % phases.length];
    if (!currentPhaseData) return 0;

    return (
      currentPhaseData.start +
      currentPhaseData.durationMs * clamp01(phaseProgressRatio)
    );
  }, [isActive, cycleData, phaseIndex, phaseProgressRatio]);

  const totalTime = isActive
    ? totalCyclesRef.current * cycleData.totalDuration + currentCycleTime
    : 0;

  // Generate paths
  const { pastPath, futurePath, heartY } = useMemo(() => {
    const heartY = getY(totalTime, cycleData);

    // Generate points
    // Range: [totalTime - history, totalTime + future]
    // History: CENTER_X / PIXELS_PER_MS
    // Future: (VIEWBOX_WIDTH - CENTER_X) / PIXELS_PER_MS

    const historyMs = CENTER_X / PIXELS_PER_MS;
    const futureMs = (VIEWBOX_WIDTH - CENTER_X) / PIXELS_PER_MS;

    const startTime = totalTime - historyMs;
    const endTime = totalTime + futureMs;

    const stepMs = 50; // Resolution

    const pastPoints: string[] = [];
    const futurePoints: string[] = [];

    // Generate Past (Left of Heart)
    // From startTime to totalTime
    for (let t = startTime; t <= totalTime; t += stepMs) {
      const x = CENTER_X + (t - totalTime) * PIXELS_PER_MS;
      const y = getY(t, cycleData);
      pastPoints.push(`${x.toFixed(1)} ${y.toFixed(1)}`);
    }
    // Ensure the point at heart is included exactly
    pastPoints.push(`${CENTER_X} ${heartY.toFixed(1)}`);

    // Generate Future (Right of Heart)
    // From totalTime to endTime
    futurePoints.push(`${CENTER_X} ${heartY.toFixed(1)}`);
    for (let t = totalTime + stepMs; t <= endTime; t += stepMs) {
      const x = CENTER_X + (t - totalTime) * PIXELS_PER_MS;
      const y = getY(t, cycleData);
      futurePoints.push(`${x.toFixed(1)} ${y.toFixed(1)}`);
    }
    // Ensure end point
    const endX = CENTER_X + (endTime - totalTime) * PIXELS_PER_MS;
    const endY = getY(endTime, cycleData);
    futurePoints.push(`${endX.toFixed(1)} ${endY.toFixed(1)}`);

    const buildPath = (points: string[]) => {
      if (points.length === 0) return '';
      return `M${points[0]} L${points.slice(1).join(' L')}`;
    };

    return {
      pastPath: buildPath(pastPoints),
      futurePath: buildPath(futurePoints),
      heartY,
    };
  }, [totalTime, cycleData]);

  const displayLabel = isActive
    ? phaseLabel
    : introVoiceOn
      ? "Let's Begin in"
      : "Let's Begin";

  const displaySeconds = Math.max(
    0,
    Math.ceil(isActive ? timeRemaining : introTimeRemaining)
  );

  return (
    <div
      data-current-phase={currentPhase}
      className="relative mx-auto flex w-[90%] max-w-[min(95vw,64rem)] flex-col gap-6 sm:w-1/2"
    >
      <div className="rounded-[32px] bg-white/70 p-0 shadow-2xl sm:p-0">
        <div className="relative h-[260px] w-full overflow-hidden rounded-3xl bg-white/10 sm:h-[320px] md:h-[340px]">
          <div className="absolute inset-x-4 top-3 flex flex-col items-center gap-1 text-center sm:inset-x-6 sm:top-1">
            <p className="text-2xl font-bold text-slate-900 sm:text-3xl">
              {displayLabel}
            </p>
            {(isActive || introVoiceOn) && (
              <p className="text-3xl font-light tabular-nums text-slate-700 sm:text-4xl">
                {displaySeconds}
                <span className="text-xl text-slate-500 sm:text-2xl">s</span>
              </p>
            )}
          </div>

          <div className="absolute bottom-0 left-0 right-0 top-[118px] sm:bottom-0 sm:top-8">
            <svg
              viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
              preserveAspectRatio="none"
              className="h-full w-full text-white/70"
              role="img"
              aria-label="Breathing line visualization"
            >
              <defs>
                <linearGradient
                  id={`${gradientId}-stroke`}
                  x1="0"
                  y1="0"
                  x2={`${CENTER_X}`}
                  y2="0"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor={gradientStops.from} />
                  <stop offset="100%" stopColor={gradientStops.to} />
                </linearGradient>
              </defs>

              {/* Future Path (Neutral) - mobile (thicker) */}
              <path
                d={futurePath}
                fill="none"
                stroke="#ffffff"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="block opacity-50 sm:hidden"
              />

              {/* Future Path (Neutral) - desktop */}
              <path
                d={futurePath}
                fill="none"
                stroke="#ffffff"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="hidden opacity-50 sm:block"
              />

              {/* Past Path (Gradient) - mobile (thicker) */}
              <path
                d={pastPath}
                fill="none"
                stroke={`url(#${gradientId}-stroke)`}
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="block drop-shadow-[0_3px_10px_rgba(6,182,212,0.28)] sm:hidden"
              />

              {/* Past Path (Gradient) - desktop */}
              <path
                d={pastPath}
                fill="none"
                stroke={`url(#${gradientId}-stroke)`}
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="hidden drop-shadow-[0_3px_10px_rgba(6,182,212,0.28)] sm:block"
              />

              {/* Heart Icon Fixed at Center X (mobile - larger) */}
              <g
                transform={`translate(${CENTER_X}, ${heartY}) scale(1.3)`}
                aria-hidden="true"
                className="block sm:hidden"
              >
                <path
                  d="M0,-3.6 C-2.2,-6.2 -6,-5 -6,-1.6 C-6,1.0 -3.6,3.4 0,6 C3.6,3.4 6,1.0 6,-1.6 C6,-5 2.2,-6.2 0,-3.6 Z"
                  fill="#fb7185"
                  stroke="#ffffff"
                  strokeWidth={1.6}
                  vectorEffect="non-scaling-stroke"
                />
              </g>

              {/* Heart Icon Fixed at Center X (desktop) */}
              <g
                transform={`translate(${CENTER_X}, ${heartY}) scale(1)`}
                aria-hidden="true"
                className="hidden sm:block"
              >
                <path
                  d="M0,-3.6 C-2.2,-6.2 -6,-5 -6,-1.6 C-6,1.0 -3.6,3.4 0,6 C3.6,3.4 6,1.0 6,-1.6 C6,-5 2.2,-6.2 0,-3.6 Z"
                  fill="#fb7185"
                  stroke="#ffffff"
                  strokeWidth={1.2}
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
});

export default LineBreathing;
